import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { TsForm } from '../../components/form/Form';
import { SelectInputField } from '../../components/form/SelectInput';
import { SubmitCancelButtons } from '../../components/form/SubmitCancelButtons';
import { TextFieldWithOptionalLabel } from '../../components/form/TextInput';
import { handleMutationResult } from '../../helpers/toastHelper';
import { useHolidayRegions, useTenantDefaultHolidayRegionLabel } from '../../services/holiday/useHolidayRegions';
import { Location, useUpdateLocationMutation } from '../../services/gql/graphql.generated';
import strings from './manageLocation.translations';

interface EditLocationDetailsFormProps {
  onComplete: () => void;
  location: Location;
}
interface EditLocationFields {
  name: string;
  holidayRegionCode: string;
}

export const EditLocationDetailsForm: FC<EditLocationDetailsFormProps> = ({ onComplete, location }) => {
  const holidayRegionOptions = useHolidayRegions();
  const tenantDefaultHolidayRegionLabel = useTenantDefaultHolidayRegionLabel(
    strings.locationHolidayRegion.inheritOption,
    strings.locationHolidayRegion.inheritOption
  );
  const locationHolidayRegionOptions = [{ name: tenantDefaultHolidayRegionLabel, value: '' }, ...holidayRegionOptions];
  const methods = useForm<EditLocationFields>({
    defaultValues: {
      name: location.name,
      holidayRegionCode: location.holidayRegionCode ?? ''
    }
  });

  const [{ fetching }, UpdateLocation] = useUpdateLocationMutation();

  const onSubmit = async (values: EditLocationFields) => {
    const result = await UpdateLocation({
      location: {
        id: location.id,
        name: values.name,
        active: location.active,
        holidayRegionCode: values.holidayRegionCode === '' ? undefined : values.holidayRegionCode
      }
    });

    await handleMutationResult(result, {
      successMessage: "Location successfully updated.",
      errorMessage: "Unable to edit location.",
      onSuccess: onComplete
    });
  };

  return (
    <TsForm methods={methods} onSubmit={onSubmit}>
      <div className="flex flex-col space-y-5 xl:w-96 mt-3">
        <TextFieldWithOptionalLabel
          id="name"
          name="name"
          title={strings.locationDetailsCard.name}
          validationMessage={strings.editLocationDetails.nameRequired}
          required
          placeholder={location.name}
          value={methods.getValues('name')}
          onChange={e => methods.setValue('name', e)}
        />
        <SelectInputField
          id="holidayRegionCode"
          name="holidayRegionCode"
          label={strings.locationHolidayRegion.label}
          description={strings.locationHolidayRegion.description}
          options={locationHolidayRegionOptions}
          value={methods.getValues('holidayRegionCode') ?? ''}
          onChange={(value: string) => methods.setValue('holidayRegionCode', value)}
        />
        <div className="flex items-center justify-end gap-3">
          <SubmitCancelButtons onComplete={onComplete} loading={fetching} submitText={strings.common.save} />
        </div>
      </div>
    </TsForm>
  );
};
